<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :title="$t('stock_management.reception.title')"
    :width="'80%'"
    :height="'85%'"
    @hiding="onClose"
  >
    <div style="display: flex; flex-direction: column; height: 95%">
      <DxDataGrid
        ref="receptionGrid"
        class="reception_grid"
        :data-source="receptionsDataSurce"
        :show-borders="true"
        :column-auto-width="true"
        :remote-operations="true"
        :hover-state-enabled="true"
        :show-column-headers="true"
        :repaint-changes-only="true"
        :selected-row-keys="selectedReceptionUuids"
        @selection-changed="onGridSelectionChanged($event)"
        @row-click="onRowClick"
        :paging="{ pageSize: 25 }"
        :height="'95%'"
      >
        <DxColumn
          dataField="product.name"
          :caption="$t('common.name')"
          :filter-operations="['contains']"
          :allowSorting="false"
          width="20%"
        />
        <DxColumn
          dataField="product.code"
          :caption="$t('stock_management.products.code')"
          :filter-operations="['contains']"
          :allowSorting="false"
          width="15%"
        />
        <DxColumn
          dataField="product.product_type_name"
          :caption="$t('stock_management.products.type')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="transaction_date"
          :caption="$t('stock_management.transactions.date')"
          :filter-operations="['contains']"
          dataType="date"
          :allowSorting="false"
          width="15%"
          :format="this.getDxGridDefaultDateFormat"
        />
        <DxColumn
          dataField="shipping_note_number"
          :caption="$t('stock_management.shipping_number')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="document_number"
          :caption="$t('stock_management.document.document_number')"
          :filter-operations="['contains']"
          :allowSorting="false"
          cellTemplate="documentHeaderTemplate"
          alignment="center"
          width="15%"
        />
        <template #documentHeaderTemplate="{ data }">
          <span v-if="data.data.document?.document_header?.current_model">{{ data.data.document?.document_header?.current_model }}</span>
          <span v-else>-</span>
        </template>
        <DxColumn
          dataField="quantity"
          :caption="$t('common.quantity')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          cellTemplate="quantityMuTemplate"
          width="15%"
        />
         <template #quantityMuTemplate="{ data }">
          <span v-if="data.data.quantity_mu">{{ data.data.quantity_mu }}</span>
          <span v-else>-</span>
        </template>
        <DxColumn
          dataField="unit_price"
          :caption="$t('stock_management.products.unit_price_net')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="vat_code"
          :caption="$t('stock_management.products.vat_code') + ' %'"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="always" selectAllMode="page" />
        <DxFilterRow :visible="true" />
        <DxGrouping :autoExpandAll="true" />
        <DxGroupPanel :visible="false" />
        <DxPaging :total="3500" :page-size="15" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="pageSizes"
          display-mode="compact"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :info-text="$t('common.page_info')"
        />
      </DxDataGrid>
      <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: auto; padding-top: 16px">
        <button class="btn btn-outline-primary col-lg-2 apply-btn" @click="onContinue">{{ $t("common.continue") }}</button>
      </div>
    </div>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxDataGrid,
  DxColumn,
  DxSelection,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxPaging,
  DxLookup,
  DxPager,
} from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import CustomStore from "devextreme/data/custom_store";

export default {
  name: "ReceptionSelector",
  components: {
    DxDataGrid,
    DxColumn,
    DxPopup,
    DxSelection,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
    DxLookup,
    DxPager,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: () => true,
    },
    selectedReceptionUuids: {
      type: Array,
      default: () => [],
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      receptionsDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };

          if (this.filterData && Object.keys(this.filterData).length > 0) {
            Object.assign(payload, this.filterData);
          }

          try {
            await this.getSmTransactions(payload).then((response) => {
              dataSource.data = response.data;
              dataSource.totalCount = response?.pagination?.total ?? 1;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching receptions:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
      localSelectedReceptionUuids: [...this.selectedReceptionUuids],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
  },
  watch: {
    selectedReceptionUuids: {
      deep: true,
      handler(newData) {
        this.localSelectedReceptionUuids = [...newData];
        this.$refs.receptionGrid.instance.selectRows(newData, false);
      },
    },
  },
  methods: {
    ...mapActions({
      getSmTransactions: "smTransactions/getSmTransactions",
    }),
    onClose() {
      this.$emit("close");
    },
    onContinue() {
      const selectedRows = this.$refs.receptionGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected receptions to the parent component

      this.$refs.receptionGrid.instance.clearSelection();
      this.localSelectedReceptionUuids = [];
      this.$emit("update:selectedReceptionUuids", []);
    },
    onGridSelectionChanged(e) {},
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.selectedReceptionUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.selectedReceptionUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedReceptionUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.receptionGrid.instance.selectRows(this.selectedReceptionUuids, false);
    },
  },
  mounted() {},
};
</script>
